import { useEffect, useRef } from 'react';
import { z } from 'zod';
import { getIDFromUrl, ID } from '@rossum/api-client';
import { EditPagesResponse } from '@rossum/api-client/annotations';
import { Queue } from '@rossum/api-client/queues';
import {
  EditDocumentConfig,
  EditState,
  StartEditModeOrganization,
  StartEditModeSideloads,
  useCancelEditMode,
  useConfirmEditMode,
  useStartEditMode,
} from '../../document-edit-core';
import { useQueue } from '../../queues/hooks/useQueue';
import { useAnnotation } from '../hooks/useAnnotation';
import { useOrganization } from '../hooks/useOrganization';
import { useSuggestedEdit } from '../hooks/useSuggestedEdit';
import { useSplitSessionTimers } from './useSplitSessionTimers';

// Placeholder sideloads used only before the annotation has loaded — same
// shape `useStartEditMode` expects, never surfaced to the caller.
const EMPTY_SIDELOADS: StartEditModeSideloads = {
  document: undefined,
  relations: [],
  queue: undefined,
};

// `uiSettings` is `unknown` on the api-client organization; only the two split
// feature flags matter here, and malformed settings must not throw.
const editUiSettingsSchema = z
  .object({
    features: z
      .object({
        disable_splitting: z.boolean().optional().catch(undefined),
        prefer_in_place_splitting: z.boolean().optional().catch(undefined),
      })
      .optional()
      .catch(undefined),
  })
  .catch({});

const parseEditUiSettings = (
  uiSettings: unknown
): StartEditModeOrganization['uiSettings'] =>
  editUiSettingsSchema.parse(uiSettings);

export type SplitSessionStatus = 'loading' | 'ready' | 'error';

export type UseSplitSessionParams = {
  annotationId: number;
  // See `useCancelEditMode` — resolved lazily during unmount.
  getTargetAnnotationId: () => number;
  /**
   * Called once, after a successful confirm, with the raw `EditPagesResponse`,
   * before the mutation's own `options.onSuccess`. It owns *all* post-confirm
   * navigation: the delete-all-pages redirect, the `uiOnEditConfirm`
   * destinations, and (MAT-1966) the embedded `return_url` redirect. The
   * session hook itself never navigates. By the time it runs, the edit timer
   * is stopped and the unmount cleanup will no longer cancel the started
   * annotations.
   */
  onConfirmSuccess: (
    response: EditPagesResponse,
    currentQueue: Queue | undefined
  ) => void;
};

export type UseSplitSessionResult = {
  status: SplitSessionStatus;
  error: unknown;
  config: EditDocumentConfig | undefined;
  confirm: (
    editState: EditState,
    options?: Parameters<ReturnType<typeof useConfirmEditMode>['mutate']>[1]
  ) => void;
  confirmInProgress: boolean;
};

export const useSplitSession = ({
  annotationId,
  getTargetAnnotationId,
  onConfirmSuccess,
}: UseSplitSessionParams): UseSplitSessionResult => {
  const annotationQuery = useAnnotation(annotationId);
  const { data: annotationData } = annotationQuery;

  const organizationQuery = useOrganization();
  const { data: organizationData } = organizationQuery;

  const queueId: ID | undefined = annotationData
    ? getIDFromUrl(annotationData.annotation.queue)
    : undefined;
  const queueQuery = useQueue(queueId);
  const { data: queue } = queueQuery;

  const suggestedEditUrl =
    annotationData &&
    typeof annotationData.annotation.suggestedEdit === 'string' &&
    annotationData.annotation.status !== 'split'
      ? annotationData.annotation.suggestedEdit
      : undefined;
  const suggestedEditQuery = useSuggestedEdit(suggestedEditUrl);
  const { data: suggestedEdit } = suggestedEditQuery;

  // `useStartEditMode` opens its gate as soon as the annotation and the
  // organization are there, and never re-runs (`cacheTime: 0`). The queue can
  // only be fetched once the annotation resolves, so withholding the annotation
  // until the queue is loaded is what keeps the config from being built with
  // `queue: undefined` (empty `editValueDefaults`, wrong error routing).
  const startEditAnnotation =
    annotationData && queue
      ? {
          id: annotationData.annotation.id,
          url: annotationData.annotation.url,
          queue: annotationData.annotation.queue,
          status: annotationData.annotation.status,
          suggestedEdit:
            suggestedEdit ?? annotationData.annotation.suggestedEdit ?? null,
        }
      : undefined;

  const startEditSideloads: StartEditModeSideloads = annotationData
    ? {
        document: annotationData.document,
        relations: annotationData.relations,
        queue,
      }
    : EMPTY_SIDELOADS;

  const startEditOrganization: StartEditModeOrganization | undefined =
    organizationData
      ? { uiSettings: parseEditUiSettings(organizationData.uiSettings) }
      : undefined;

  const { data: config } = useStartEditMode({
    annotation: startEditAnnotation,
    annotationSideloads: startEditSideloads,
    organization: startEditOrganization,
  });

  const status: SplitSessionStatus =
    annotationQuery.isError || organizationQuery.isError || queueQuery.isError
      ? 'error'
      : config === undefined
        ? 'loading'
        : 'ready';

  const error =
    annotationQuery.error ??
    organizationQuery.error ??
    queueQuery.error ??
    undefined;

  const { endEditTimer } = useSplitSessionTimers({
    annotationId,
    annotationUrl: config?.annotationUrl ?? '',
    mode: config?.mode ?? { type: 'single-annotation' },
    enabled: status === 'ready',
  });

  const confirmHasSucceededRef = useRef(false);

  const handleConfirmSuccess = (response: EditPagesResponse) => {
    confirmHasSucceededRef.current = true;
    onConfirmSuccess(response, queue);
  };

  const confirmMutation = useConfirmEditMode({
    currentQueue: queue,
    onSuccess: handleConfirmSuccess,
  });

  const confirm: UseSplitSessionResult['confirm'] = (state, options) => {
    endEditTimer(); // MUST run before mutate — see timeSpent.ts:211-212
    confirmMutation.mutate(state, options);
  };

  const { mutate: cancelStartedAnnotations } = useCancelEditMode({
    getTargetAnnotationId,
  });

  useEffect(() => {
    return () => {
      if (config && !confirmHasSucceededRef.current) {
        endEditTimer();
        cancelStartedAnnotations(config);
      }
    };
  }, [cancelStartedAnnotations, config, endEditTimer]);

  return {
    status,
    error,
    config,
    confirm,
    confirmInProgress: confirmMutation.isLoading,
  };
};
